import { faro } from '@grafana/faro-web-sdk';
import { getStorage } from 'common-storage';
import { AUTH_TOKEN_KEY } from 'common-types';
import { isError } from 'remeda';
import { JsonValue } from 'type-fest';

import {
  getProductRefererHeader,
  shouldAddProductRefererHeader,
} from './product-referer-header';

// import {
//   getUnauthorizedHandler,
//   isLogoutInProgressState,
//   setLogoutInProgress,
//   setUnauthorizedHandlerNull,
// } from './unauthorized-handler';

type HttpMethod = <ResponseType = unknown>(
  url: RequestInfo,
  config?: RequestInit,
) => Promise<ResponseType>;

type HttpMethodWithBody = <ResponseType = unknown>(
  url: RequestInfo,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  body?: any,
  config?: RequestInit,
) => Promise<ResponseType>;

export type HttpClient = {
  get: HttpMethod;
  post: HttpMethodWithBody;
  put: HttpMethodWithBody;
  patch: HttpMethodWithBody;
  delete: HttpMethod;
  abortSignal: () => void;
};

let controller: AbortController;

export function createHttpClient(
  baseUrl: RequestInfo,
  defaultHeaders: () => HeadersInit,
  defaultConfig?: () => RequestInit,
): HttpClient {
  return {
    get: (url, config) =>
      fetchWrapper(`${baseUrl}${url}`, {
        method: 'GET',
        ...defaultConfig?.(),
        ...config,
        headers: Object.assign({}, defaultHeaders(), config?.headers),
      }),
    post: (url, body, config) => {
      const fullUrl = `${baseUrl}${url}`;
      const productRefererHeader = shouldAddProductRefererHeader(fullUrl)
        ? getProductRefererHeader()
        : {};
      return fetchWrapper(
        fullUrl,
        buildFetchWriteConfig('POST', body, {
          ...defaultConfig?.(),
          ...config,
          headers: Object.assign(
            {},
            defaultHeaders(),
            productRefererHeader,
            config?.headers,
          ),
        }),
      );
    },
    put: (url, body, config) =>
      fetchWrapper(
        `${baseUrl}${url}`,
        buildFetchWriteConfig('PUT', body, {
          ...config,
          headers: Object.assign({}, defaultHeaders(), config?.headers),
        }),
      ),
    patch: (url, body, config) =>
      fetchWrapper(
        `${baseUrl}${url}`,
        buildFetchWriteConfig('PATCH', body, {
          ...defaultConfig?.(),
          ...config,
          headers: Object.assign({}, defaultHeaders(), config?.headers),
        }),
      ),
    delete: (url, config) =>
      fetchWrapper(`${baseUrl}${url}`, {
        method: 'DELETE',
        ...defaultConfig?.(),
        ...config,
        headers: Object.assign({}, defaultHeaders(), config?.headers),
      }),
    abortSignal: () => controller.abort(),
  };
}

export const httpClient = createHttpClient('', () => ({}));

const getHeaderValue = (
  headers: HeadersInit,
  headerKey: string,
): string | undefined => {
  if (Array.isArray(headers)) {
    const header = headers.find(
      ([key]) => key.toLowerCase() === headerKey.toLowerCase(),
    );
    return header ? header[1] : undefined;
  } else if (headers instanceof Headers) {
    const value = headers.get(headerKey);
    return value !== null ? value : undefined;
  } else {
    return headers[headerKey];
  }
};

// Diagnostic-only: log every 401 we see (without acting on it) so we can
// confirm in production that 401s are genuine session failures - not the
// empty-token / duplicate-concurrent-request races that caused the global
// 401 handler below to be disabled - before re-enabling it (see
// PE-13575-refine-401-handling-prevent-unintended-session-termination).
// Fire-and-forget: must not delay or change the error being thrown.
const logUnauthorizedDiagnostic = (
  url: RequestInfo,
  headers: HeadersInit | undefined,
) => {
  void (async () => {
    try {
      const sentAuthHeader = headers
        ? getHeaderValue(headers, 'Authorization')
        : undefined;
      const tokenInStorageNow = await getStorage().getItem(AUTH_TOKEN_KEY);
      const context = {
        url: url.toString(),
        hadAuthorizationHeader: String(Boolean(sentAuthHeader)),
        sentTokenMatchesCurrentStorage: String(
          Boolean(
            sentAuthHeader &&
              tokenInStorageNow &&
              sentAuthHeader === `Bearer ${tokenInStorageNow}`,
          ),
        ),
      };
      console.warn(
        'HttpClient: 401 received (diagnostic only, no action taken):',
        context,
      );
      // pushLog (not pushEvent - unused anywhere else in this codebase, and
      // unproven against the existing Grafana setup). pushLog with a
      // structured context matches the established pattern, see
      // faro-measurement-performance-observer.tsx, and gives queryable
      // fields instead of relying on parsing the auto-captured console.warn.
      if (faro?.api?.pushLog) {
        faro.api.pushLog(['http_401_diagnostic'], { context });
      }
    } catch (loggingError) {
      console.warn('HttpClient: failed to log 401 diagnostic:', loggingError);
    }
  })();
};

const fetchWrapper = async <ResponseType = unknown>(
  url: RequestInfo,
  config: // eslint-disable-next-line @typescript-eslint/no-explicit-any
  RequestInit & { body?: any },
): Promise<ResponseType> => {
  try {
    const requestController = new AbortController();

    const actualConfig: RequestInit = {
      credentials: 'omit',
      ...config,
    };

    const isFormData =
      typeof FormData !== 'undefined' && config.body instanceof FormData;
    if (config.body && !isFormData) {
      actualConfig.body = JSON.stringify(config.body);
    }

    controller = requestController;

    const response = await fetch(url, {
      ...actualConfig,
      credentials: 'omit',
      signal: actualConfig.signal ?? controller.signal,
    });

    if (!response.ok) {
      const error = await HttpError.build(url, actualConfig, response);
      throw error;
    }

    // try to parse response as JSON. If it fails, return the raw response string
    const text = await response.text();
    try {
      const json = JSON.parse(text);
      return json;
    } catch {
      return text as unknown as ResponseType;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    if (isHttpError(error as Error)) {
      error.logToConsole && error.logToConsole();
    } else {
      const entryType = 'navigation';
      let navigationEntryType: PerformanceEntry | undefined;
      if (PerformanceObserver.supportedEntryTypes.includes(entryType)) {
        const [navigationEntry] = performance.getEntriesByType(entryType);
        navigationEntryType = navigationEntry;
      }
      setTimeout(() => {
        console.error(
          'HttpError: ',
          JSON.stringify({
            url: url,
            navigationEntryType: navigationEntryType || 'unknown',
            isOnline: `${navigator.onLine}`,
            error: isError(error) ? error.message : JSON.stringify(error),
          }),
        );
      }, 100);
    }

    // Handle 401 Unauthorized errors
    if (
      error.response?.status === 401 &&
      // eslint-disable-next-line node/no-process-env
      url.toString().includes(process.env.NEXT_PUBLIC_API_BASE_URL!)
    ) {
      logUnauthorizedDiagnostic(url, config.headers);
    }
    // if (
    //   error.response?.status === 401 &&
    //   // eslint-disable-next-line node/no-process-env
    //   url.toString().includes(process.env.NEXT_PUBLIC_API_BASE_URL!)
    // ) {
    //   if (config.headers) {
    //     //For Extension: 401 will happen in backround script where unauthorized handler is null, we will handle it in content script
    //     const xProduct = getHeaderValue(config.headers, 'X-Product');
    //     if (xProduct === 'paperpal-edit-for-extension') {
    //       throw error;
    //     }
    //   }

    //   // Atomic check-and-set: capture and null the handler immediately
    //   // Only the FIRST 401 will capture a non-null handler
    //   const capturedHandler = getUnauthorizedHandler();
    //   if (capturedHandler && !isLogoutInProgressState()) {
    //     setUnauthorizedHandlerNull(); // Immediately null it to block other concurrent 401s
    //     setLogoutInProgress(true);
    //     capturedHandler();
    //   }

    //   throw new Error('Session expired. Please login again.');
    // }

    if (error.name === 'AbortError') {
      throw new Error('Request has been cancelled');
    }

    throw error;
  }
};

const buildFetchWriteConfig = (
  method: 'POST' | 'PUT' | 'PATCH',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  body?: any,
  config?: RequestInit,
) => {
  const isFormData =
    typeof FormData !== 'undefined' && body instanceof FormData;

  return {
    method,
    body,
    ...config,
    headers: {
      ...(body && !isFormData ? { 'Content-Type': 'application/json' } : {}),
      ...(config && config.headers),
    },
  };
};

export function isHttpError(error: Error): error is HttpError {
  return error instanceof HttpError;
}

export class HttpError extends Error {
  public url!: RequestInfo;
  public requestConfig!: RequestInit;
  public response!: Record<string, unknown> & { status: number };
  public rawResponse!: Response;
  public text!: string;
  public json: JsonValue = {};

  private constructor(message: string) {
    super(message);
    this.name = 'HttpError';
  }

  public static async build(
    url: RequestInfo,
    requestConfig: RequestInit,
    rawResponse: Response,
  ): Promise<HttpError> {
    const text = await rawResponse.text();
    const message = `${rawResponse.status} ${rawResponse.statusText}: ${text}`;
    const error = new HttpError(message);
    error.url = url;
    error.requestConfig = requestConfig;
    error.rawResponse = rawResponse;
    // converts the response into a plain object so it can be logged in the console and added to the DOM
    error.response = {
      headers: Object.fromEntries(rawResponse.headers),
      ok: rawResponse.ok,
      redirected: rawResponse.redirected,
      status: rawResponse.status,
      statusText: rawResponse.statusText,
      type: rawResponse.type,
      url: rawResponse.url,
    };
    error.text = text;
    try {
      error.json = JSON.parse(text);
    } catch {}

    return error;
  }

  public withPayload(payload: JsonValue): HttpError {
    this.json = payload;
    return this;
  }

  public logToConsole() {
    const { rawResponse, ...object } = this;
    if (!filterKnownErrors(object)) {
      // eslint-disable-next-line no-console
      console.error(this.name, JSON.stringify(object));
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function filterKnownErrors(object: any) {
  if (
    object.json?.errorCode &&
    object.json?.errorCode === 'BIGINT_ID_ALREADY_EXISTS'
  ) {
    return true;
  }
  return false;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ApiRequest<T = any> = {
  type: string;
  endpoint: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  payload?: T;
  isFormData?: boolean;
  formDataEntries?: Array<
    [
      string,
      (
        | string
        | { type: 'file'; name: string; content: string; fileType: string }
      ),
    ]
  >;
  parseSchema?: string;
  baseUrl?: string;
  tokenStorageKey: string;
  hostname?: string;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ApiResponse<T = any> = {
  success: boolean;
  data?: T;
  error?: unknown;
};

export interface SerializedHttpError {
  name: string;
  message: string;
  url?: string;
  requestConfig?: RequestInit;
  response?: {
    status?: number;
    statusText?: string;
    headers?: Record<string, string>;
    [key: string]: unknown;
  };
  text?: string;
  json?: JsonValue;
  [key: string]: unknown;
}

// export const handleHttpErrorForExtension = async (
//   error: unknown,
//   requestUrl?: string,
//   method?: string,
// ) => {
//   if (
//     typeof error === 'object' &&
//     error !== null &&
//     'name' in error &&
//     error.name === 'HttpError'
//   ) {
//     const httpErrorData = error as SerializedHttpError;

//     const mockResponse = new Response(httpErrorData.text || '', {
//       status: httpErrorData.response?.status,
//       statusText: httpErrorData.response?.statusText || 'Error',
//       headers: httpErrorData.response?.headers || {},
//     });

//     const httpError = await HttpError.build(
//       httpErrorData.url || requestUrl || '',
//       httpErrorData.requestConfig || { method } || {},
//       mockResponse,
//     );

//     if (httpErrorData.json) {
//       httpError.withPayload(httpErrorData.json);
//     }

//     // Handle 401 errors in extension content script context
//     // The background script couldn't handle it because unauthorizedHandler was null there
//     if (
//       httpErrorData.response?.status === 401 &&
//       httpErrorData.url?.toString().includes(import.meta.env.WXT_API_BASE_URL!)
//     ) {
//       const capturedHandler = getUnauthorizedHandler();
//       if (capturedHandler && !isLogoutInProgressState()) {
//         setUnauthorizedHandlerNull(); // Immediately null it to block other concurrent 401s
//         setLogoutInProgress(true);
//         capturedHandler();
//       }
//       throw new Error('Session expired. Please login again.');
//     }

//     throw httpError;
//   }

//   throw error;
// };

async function sendBackgroundRequest<ResponseType>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  endpoint: string,
  tokenStorageKey: string,
  hostname: string,
  payload?: unknown,
  baseUrl?: string,
  clientType?: string,
): Promise<ResponseType> {
  try {
    // Lazy load common-messaging to avoid loading browser extension code in non-browser environments
    const { sendMessage } = await import('common-messaging');

    let processedPayload = payload;
    let isFormData = false;
    let formDataEntries: Array<
      [
        string,
        (
          | string
          | { type: 'file'; name: string; content: string; fileType: string }
        ),
      ]
    > = [];

    // Helper function to check if value is a Blob (but not a File)
    const isBlob = (value: unknown): value is Blob => {
      return (
        typeof Blob !== 'undefined' &&
        value instanceof Blob &&
        !(value instanceof File)
      );
    };

    // Check if payload is FormData and serialize it
    if (typeof FormData !== 'undefined' && payload instanceof FormData) {
      isFormData = true;
      formDataEntries = [];

      // Convert FormData to serializable format
      for (const [key, value] of payload.entries()) {
        if (value instanceof File) {
          // Check file size (warn if > 25MB, as this might cause issues with serialization)
          if (value.size > 25 * 1024 * 1024) {
            console.warn(
              `Large file detected (${Math.round(
                value.size / 1024 / 1024,
              )}MB): ${value.name}. This may cause performance issues.`,
            );
          }

          // Convert File to base64 string for serialization
          const fileContent = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(value);
          });

          formDataEntries.push([
            key,
            {
              type: 'file',
              name: value.name,
              content: fileContent,
              fileType: value.type,
            },
          ]);
        } else if (isBlob(value)) {
          // Check blob size
          if (value.size > 25 * 1024 * 1024) {
            console.warn(
              `Large blob detected (${Math.round(
                value.size / 1024 / 1024,
              )}MB). This may cause performance issues.`,
            );
          }

          // Handle Blob objects (similar to File but without name)
          const fileContent = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(value);
          });

          formDataEntries.push([
            key,
            {
              type: 'file',
              name: `blob_${Date.now()}`, // Generate a name for the blob
              content: fileContent,
              fileType: value.type || 'application/octet-stream',
            },
          ]);
        } else {
          // Handle strings and other primitive values
          // Convert to string to ensure serialization works
          // Handle null/undefined by converting to empty string
          const stringValue =
            value === null || value === undefined ? '' : String(value);
          formDataEntries.push([key, stringValue]);
        }
      }

      // Don't send the FormData object itself as it's not serializable
      processedPayload = undefined;
    }

    const result = await sendMessage('webApi', {
      type: clientType || 'default',
      endpoint,
      method,
      payload: processedPayload,
      isFormData,
      formDataEntries,
      baseUrl,
      tokenStorageKey,
      hostname,
    });

    if (result.success) {
      return result.data as ResponseType;
    } else {
      const error = result.error;
      if (
        typeof error === 'object' &&
        error !== null &&
        'name' in error &&
        error.name === 'HttpError'
      ) {
        // const httpError = error as HttpError;
        // if (
        //   httpError.response?.status === 401 &&
        //   baseUrl?.includes(import.meta.env.WXT_API_BASE_URL!)
        // ) {
        //   const capturedHandler = getUnauthorizedHandler();
        //   if (capturedHandler && !isLogoutInProgressState()) {
        //     setUnauthorizedHandlerNull();
        //     setLogoutInProgress(true);
        //     capturedHandler();
        //   }
        //   throw new Error('Session expired. Please login again.');
        // }

        const httpErrorData = error as SerializedHttpError;
        const requestUrl = `${baseUrl || ''}${endpoint}`;

        const mockResponse = new Response(httpErrorData.text || '', {
          status: httpErrorData.response?.status,
          statusText: httpErrorData.response?.statusText || 'Error',
        });

        const httpError = await HttpError.build(
          httpErrorData.url || requestUrl,
          httpErrorData.requestConfig || { method },
          mockResponse,
        );

        if (httpErrorData.json) {
          httpError.withPayload(httpErrorData.json);
        }

        throw httpError;
      }
      throw error;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(`Extension API request failed: ${endpoint}`, error);
    if (isHttpError(error as Error)) {
      error.logToConsole && error.logToConsole();
    }
    if (error.name === 'AbortError') {
      throw new Error('Request has been cancelled');
    }

    throw error;
  }
}

export function createExtensionHttpClient(
  baseUrl: RequestInfo,
  tokenStorageKey: string,
  clientType = 'web',
  hostname = '',
): HttpClient {
  return {
    get: async <ResponseType = unknown>(
      url: RequestInfo,
    ): Promise<ResponseType> => {
      return sendBackgroundRequest<ResponseType>(
        'GET',
        url.toString(),
        tokenStorageKey,
        hostname,
        undefined,
        baseUrl.toString(),
        clientType,
      );
    },

    post: async <ResponseType = unknown>(
      url: RequestInfo,
      body?: unknown,
    ): Promise<ResponseType> => {
      return sendBackgroundRequest<ResponseType>(
        'POST',
        url.toString(),
        tokenStorageKey,
        hostname,
        body,
        baseUrl.toString(),
        clientType,
      );
    },

    put: async <ResponseType = unknown>(
      url: RequestInfo,
      body?: unknown,
    ): Promise<ResponseType> => {
      return sendBackgroundRequest<ResponseType>(
        'PUT',
        url.toString(),
        tokenStorageKey,
        hostname,
        body,
        baseUrl.toString(),
        clientType,
      );
    },

    patch: async <ResponseType = unknown>(
      url: RequestInfo,
      body?: unknown,
    ): Promise<ResponseType> => {
      return sendBackgroundRequest<ResponseType>(
        'PATCH',
        url.toString(),
        tokenStorageKey,
        hostname,
        body,
        baseUrl.toString(),
        clientType,
      );
    },

    delete: async <ResponseType = unknown>(
      url: RequestInfo,
    ): Promise<ResponseType> => {
      return sendBackgroundRequest<ResponseType>(
        'DELETE',
        url.toString(),
        tokenStorageKey,
        hostname,
        undefined,
        baseUrl.toString(),
        clientType,
      );
    },

    abortSignal: () => {
      // Not supported in extension environment
      console.warn('abortSignal is not supported in extension environment');
    },
  };
}
